import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HomePage } from './components/HomePage.jsx';

const isBrowser = typeof window !== 'undefined';

// Lazy load the GameApp for code splitting
const GameApp = lazy(() => import('./components/GameApp.jsx'));

// CSS Animations
const animationStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slide-down {
    from { opacity: 0; transform: translateY(-10px); max-height: 0; }
    to { opacity: 1; transform: translateY(0); max-height: 1000px; }
  }

  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.1); }
  }

  .animate-float { animation: float 3s ease-in-out infinite; }

  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient-shift 3s ease infinite;
  }

  .animate-fade-in { animation: fade-in 0.6s ease-out; }
  .animate-fade-in-delay-1 { animation: fade-in 0.6s ease-out 0.2s both; }
  .animate-fade-in-delay-2 { animation: fade-in 0.6s ease-out 0.4s both; }
  .animate-fade-in-delay-3 { animation: fade-in 0.6s ease-out 0.6s both; }

  .animate-slide-down { animation: slide-down 0.3s ease-out; }

  .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
`;

export default function App() {
  const [playerName, setPlayerName] = useState('');
  const [playerAvatar, setPlayerAvatar] = useState('🐶');
  const [roomCode, setRoomCode] = useState('');
  const [startConfig, setStartConfig] = useState(null); // { action: 'create' | 'join' | 'local', playerData }

  // Load saved nickname after hydration
  useEffect(() => {
    if (!isBrowser) return;
    const savedName = localStorage.getItem('imposter_name');
    if (savedName) setPlayerName(savedName);
  }, []);

  // Initialize AdSense
  useEffect(() => {
    if (!isBrowser) return;
    try {
      if (window.adsbygoogle) {
        const ads = document.querySelectorAll('.adsbygoogle');
        ads.forEach((ad) => {
          if (!ad.dataset.adsbygoogleStatus) {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          }
        });
      }
    } catch (err) {
      console.log('AdSense error:', err);
    }
  }, []);

  const startWithAction = (action) => {
    setStartConfig({
      action,
      playerData: {
        name: playerName.trim(),
        avatar: playerAvatar,
        roomCode: roomCode.trim().toUpperCase()
      }
    });
  };

  const renderShell = (content) => (
    <>
      <style>{animationStyles}</style>
      <div className="bg-gray-950 text-white font-sans selection:bg-indigo-500/30">
        <div className="w-full shadow-2xl relative overflow-hidden min-h-screen">
          {/* Use absolute positioning instead of fixed to avoid blocking Auto Ads */}
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-600/20 blur-[100px] rounded-full pointer-events-none -z-10"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-600/20 blur-[100px] rounded-full pointer-events-none -z-10"></div>
          <div className="relative z-0">
            {content}
          </div>
        </div>
      </div>
    </>
  );

  if (!startConfig) {
    return renderShell(
      <HomePage
        playerName={playerName}
        playerAvatar={playerAvatar}
        roomCode={roomCode}
        onAvatarChange={setPlayerAvatar}
        onNameChange={setPlayerName}
        onRoomCodeChange={setRoomCode}
        onCreate={() => startWithAction('create')}
        onJoin={() => startWithAction('join')}
        onLocal={() => startWithAction('local')}
      />
    );
  }

  return renderShell(
    isBrowser && (
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-screen">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        }
      >
        <GameApp
          initialPlayerData={startConfig.playerData}
          initialAction={startConfig.action}
        />
      </Suspense>
    )
  );
}
